import { useContext, useMemo } from "react"

import { NetworksStateContext, NetworksDispatchContext, NetworksState, NetworksDispatch } from "./NetworksContext.context"

export const useNetworksStateContext = (): NetworksState => {
    const context = useContext(NetworksStateContext)
    if (context === undefined) {
        throw new Error("useNetworksStateContext must be used within an NetworksContextProvider")
    }
    return context
}

export const useNetworksDispatchContext = (): NetworksDispatch => {
    const context = useContext(NetworksDispatchContext)
    if (context === undefined) {
        throw new Error("useNetworksDispatchContext must be used within an NetworksContextProvider")
    }
    return context
}

export const useNetworksContext = (): [NetworksState, NetworksDispatch] => {
    return [useNetworksStateContext(), useNetworksDispatchContext()]
}

export const useSubNetworksWithWriteAccess = (networkSubs?: string[], module?: string) => {
    const context = useContext(NetworksStateContext)
    if (context === undefined) {
        throw new Error("useSubNetworksWithWriteAccess must be used within an NetworksContextProvider")
    }
    const networkHasSubNetworks = context.selectedNetwork?.["children"]?.length
    const selectedSubNetworks = networkSubs?.length ? networkSubs : (networkHasSubNetworks ? context?.selectedSubNetworkSlugs : null)
    const childrenKey = context.selectedNetwork?.["children"]
        ?.map(child => `${child.slug}:${child.role}:${child.modules?.join("|")}`)
        .join(",")

    return useMemo(() => {
        if (selectedSubNetworks) {
            return selectedSubNetworks.filter(subNetwork => {
                const child = context.selectedNetwork?.["children"]?.find(selectedNetworkChild => {
                    return selectedNetworkChild.slug === subNetwork
                })
                return child?.role !== "READER" && (!module || child?.modules?.includes(module))
            })
        } else {
            return context.selectedNetwork?.["role"] !== "READER" ? [context.selectedNetwork.slug] : []
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSubNetworks?.join(","), childrenKey, context.selectedNetwork?.slug, context.selectedNetwork?.["role"], module])
}

export const useRoleIsReadOnly = (subNetworksToCheck?: string[], module?: string) => {
    const networkRightsForSubNetwork = useSubNetworksWithWriteAccess(subNetworksToCheck, module)
    const networkRightsForAllSubNetworks = useSubNetworksWithWriteAccess(undefined, module)
    if (subNetworksToCheck?.length) {
        return networkRightsForSubNetwork.every(s => !subNetworksToCheck.includes(s))
    } else {
        return !networkRightsForAllSubNetworks.length
    }
}

export const useIsModuleAvailableForItem = (subNetworks?: string[], module?: string) => {
    const { selectedNetwork } = useNetworksStateContext()

    return selectedNetwork?.children?.length ? selectedNetwork.children
        .filter(child => subNetworks?.includes(child.slug) ?? false)
        .some(child => child.modules.includes(module))
        : selectedNetwork?.modules?.includes(module)
}